// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AssemblyFilter,
  AssemblySortProperty,
} from '@/assembly/api/get-assemblies';
import { Assembly } from '@/assembly/api/types';
import { AssemblyTableRow } from '@/assembly/components/assembly-table-row';
import { useAssemblies } from '@/assembly/hooks/use-assemblies';
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';

interface Props {
  sort: Sort<AssemblySortProperty>;
  filter?: AssemblyFilter;
  onPageChange: (pageNumber: number) => void;
  onSort: (property: AssemblySortProperty) => void;
  handleRegisterAssembly: (assembly: Assembly) => void;
}

const pageSize = 50;

export function AssemblyTable(props: Props) {
  const { assemblies, resultCount } = useAssemblies({
    filter: props.filter,
  });

  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  const pageNumber = props.filter?.page ?? 1;
  const onPageChange = props.onPageChange;

  const createSortHandler = (column: AssemblySortProperty) => () => {
    props.onSort(column);
  };

  useEffect(() => {
    if (onPageChange && assemblies.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [assemblies.length, pageNumber, onPageChange]);

  if (assemblies.length === 0) {
    return (
      <EmptyState title="No assemblies found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking assemblies in Hardfin, add a new assembly
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="20%">References</TableCell>
              <TableCell
                width="20%"
                sortDirection={getSortDirection('product')}
              >
                <TableSortLabel
                  active={isLabelActive('product')}
                  direction={getLabelDirection('product')}
                  onClick={createSortHandler('product')}
                >
                  Product
                </TableSortLabel>
              </TableCell>
              <TableCell
                width="20%"
                sortDirection={getSortDirection('location')}
              >
                <TableSortLabel
                  active={isLabelActive('location')}
                  direction={getLabelDirection('location')}
                  onClick={createSortHandler('location')}
                >
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell
                width="15%"
                sortDirection={getSortDirection('buildDate')}
              >
                <TableSortLabel
                  active={isLabelActive('buildDate')}
                  direction={getLabelDirection('buildDate')}
                  onClick={createSortHandler('buildDate')}
                >
                  Build date
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assemblies.map((assembly: Assembly) => (
              <AssemblyTableRow
                key={assembly.id}
                assembly={assembly}
                handleRegisterAssembly={props.handleRegisterAssembly}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{
          singular: 'assembly',
          plural: 'assemblies',
          includeCount: false,
        }}
      />
    </>
  );
}
